import { diff3MergeRegions } from "node-diff3"
import { assembleBuffer, createHTMLComparisonArray } from "routes/schedule/compare/lib/diff3/utilities"
import { markFormattedWords, stripFormattedWordMarkers } from "routes/schedule/compare/lib/diff3/mark-formatted-words"

export const PDF_ADDED_COLOR = "#1d4ed8"
export const PDF_REMOVED_COLOR = "#dc2626"
const PDF_REMOVED_BG = "#fee2e2"

/**
 * Word-level diff of `text` against `original`/`other`, returning `text` as HTML with the changed
 * words highlighted (blue when added, red strikethrough when `showRemoved`). A word whose
 * formatting changed counts as changed. A list item whose own text is all highlighted gets the
 * `pdf-diff-added` or `pdf-diff-removed` class, so its number or bullet can be highlighted too.
 *
 * @param {{ text: string, original: string, other: string, showRemoved?: boolean }} params
 * @returns {string}
 */
export function buildPdfHtmlDiff({ text, original, other, showRemoved = false }) {
    if (!text) return ""

    const regions = diff3MergeRegions(
        createHTMLComparisonArray(markFormattedWords(text)),
        createHTMLComparisonArray(markFormattedWords(original)),
        createHTMLComparisonArray(markFormattedWords(other))
    )
    const output = []

    for (const region of regions) {
        if (region.stable) {
            if (region.bufferContent.length === 1 && region.bufferContent[0] === "\n") {
                region.buffer = "o"
            }

            if (region.buffer === "a") {
                output.push(highlightWords(region.bufferContent, buildDiffSpanTemplate({ removed: showRemoved })))
            } else {
                output.push(assembleBuffer(region.bufferContent, "INSERTHERE", true))
            }
            continue
        }

        if (region.aContent?.length > 0) {
            output.push(highlightWords(region.aContent, buildDiffSpanTemplate({ removed: showRemoved })))
        }
    }

    return markChangedListItems(stripFormattedWordMarkers(output.join(" ")), showRemoved)
}

// List numbers and bullets are drawn by CSS, not text, so the word highlighting can't reach them.
// Tag each item whose own text was all highlighted, so the PDF styles can colour its number too.
function markChangedListItems(html, showRemoved) {
    if (!/<li\b/i.test(html) || typeof document === "undefined") return html

    const container = document.createElement("div")
    container.innerHTML = html
    for (const item of container.querySelectorAll("li")) {
        if (isWhollyHighlighted(item)) item.classList.add(showRemoved ? "pdf-diff-removed" : "pdf-diff-added")
    }
    return container.innerHTML
}

function isWhollyHighlighted(item) {
    const ownText = collectOwnTextNodes(item).filter((node) => node.nodeValue.trim())
    return ownText.length > 0 && ownText.every((node) => node.parentElement.closest("[data-pdf-diff]"))
}

// The item's text, leaving out any list nested inside it, which has numbers of its own
function collectOwnTextNodes(item) {
    const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT)
    const nodes = []
    while (walker.nextNode()) {
        if (walker.currentNode.parentElement.closest("li") === item) nodes.push(walker.currentNode)
    }
    return nodes
}

// Wraps each stretch of changed words in its own span and leaves the tags outside. A span wrapped
// around a tag such as <p> can't be closed by the browser, so the highlight would run on over all
// the text after it. That happens whenever the published text uses <br> where the draft uses <p>.
function highlightWords(tokens, template) {
    const output = []
    let words = []

    for (const token of tokens) {
        if (isTag(token)) {
            flushWords()
            output.push(token)
            continue
        }
        words.push(token)
    }
    flushWords()

    return output.join(" ")

    function flushWords() {
        if (words.some((word) => word.trim())) {
            output.push(template.replace(/INSERTHERE/g, words.join(" ")))
        } else {
            output.push(...words)
        }
        words = []
    }
}

function isTag(token) {
    return /^<[^>]+>$/.test(token)
}

function buildDiffSpanTemplate({ removed }) {
    if (removed) {
        return `<span data-pdf-diff style="color:${PDF_REMOVED_COLOR};background:${PDF_REMOVED_BG};text-decoration:line-through;text-decoration-thickness:1px;">INSERTHERE</span>`
    }

    return `<span data-pdf-diff style="color:${PDF_ADDED_COLOR};">INSERTHERE</span>`
}
